// pushNotifications.js - Service for managing push notification subscriptions
import { API_URL } from '../config';

/**
 * Check if push notifications are supported
 * On iOS, PushManager is only available when installed as PWA
 * iOS 26 / Safari 26: All sites added to homescreen are web apps by default
 */
export const isPushSupported = () => {
  // Basic check
  if (!('serviceWorker' in navigator)) {
    console.log('[Push] Service workers not supported');
    return false;
  }

  // Check for Notification API
  if (!('Notification' in window)) {
    console.log('[Push] Notification API not supported');
    return false;
  }

  // Check for PushManager - this is the definitive check
  if ('PushManager' in window) {
    console.log('[Push] PushManager available');
    return true;
  }

  // On iOS, PushManager may only be available after service worker is ready
  // iOS 26: All homescreen sites are web apps, but PushManager still requires
  // the site to be running in standalone/fullscreen mode
  const iosDevice = isIOS();
  const standalone = isInstalledPWA();

  console.log('[Push] iOS:', iosDevice, 'Standalone:', standalone, 'PushManager:', 'PushManager' in window);

  // If on iOS but not in standalone mode, push won't work
  if (iosDevice && !standalone) {
    console.log('[Push] iOS detected but not in standalone mode - push not available');
    return false;
  }

  // If we're on iOS in standalone mode but no PushManager, it might be an older iOS
  if (iosDevice && standalone && !('PushManager' in window)) {
    console.log('[Push] iOS standalone but no PushManager - iOS version may not support web push');
    return false;
  }

  console.log('[Push] PushManager not available');
  return false;
};

/**
 * Check if the app is running as an installed PWA
 */
export const isInstalledPWA = () => {
  // Check display-mode media query
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari uses navigator.standalone
  const isIOSStandalone = window.navigator.standalone === true;
  // iOS 26+: Also check fullscreen mode
  const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;

  console.log('[Push] Standalone:', isStandalone, 'iOS standalone:', isIOSStandalone, 'Fullscreen:', isFullscreen);
  return isStandalone || isIOSStandalone || isFullscreen;
};

/**
 * Async check for push support - waits for service worker to be ready
 * This is more reliable on iOS where PushManager may only be available after SW registration
 * On iOS, pushManager only appears on the serviceWorker registration object AFTER
 * the site has been added to the home screen
 */
export const isPushSupportedAsync = async () => {
  // Basic checks first
  if (!('serviceWorker' in navigator)) {
    console.log('[Push Async] Service workers not supported');
    return false;
  }

  if (!('Notification' in window)) {
    console.log('[Push Async] Notification API not supported');
    return false;
  }

  // On iOS, check if running as installed PWA first
  const iosDevice = isIOS();
  const standalone = window.navigator.standalone === true;

  if (iosDevice && !standalone) {
    console.log('[Push Async] iOS detected but not running as home screen app');
    console.log('[Push Async] navigator.standalone:', window.navigator.standalone);
    return false;
  }

  try {
    // Register/get service worker first
    let registration;
    try {
      registration = await navigator.serviceWorker.register('/sw.js');
      console.log('[Push Async] Service worker registered:', registration.scope);
    } catch (regError) {
      console.log('[Push Async] SW register failed, trying ready:', regError);
      registration = await navigator.serviceWorker.ready;
    }

    // Wait a moment for iOS to initialize pushManager
    await new Promise(resolve => setTimeout(resolve, 100));

    // The critical check for iOS: pushManager on the registration object
    if (registration.pushManager) {
      console.log('[Push Async] pushManager available on registration object');
      return true;
    }

    // Fallback: check window.PushManager (works on desktop browsers)
    if ('PushManager' in window) {
      console.log('[Push Async] PushManager available in window');
      return true;
    }

    console.log('[Push Async] No pushManager found on registration or window');
    console.log('[Push Async] registration.pushManager:', registration.pushManager);
    return false;
  } catch (error) {
    console.error('[Push Async] Error checking push support:', error);
    return false;
  }
};

/**
 * Check if running on iOS
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Get detailed support info for debugging
 */
export const getPushSupportInfo = () => {
  const standalone = window.matchMedia('(display-mode: standalone)').matches;
  const fullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
  // This is the key iOS check - only true when running as home screen app
  const iosStandalone = window.navigator.standalone;

  return {
    // Core APIs
    serviceWorker: 'serviceWorker' in navigator,
    notification: 'Notification' in window,
    pushManagerInWindow: 'PushManager' in window,
    notificationPermission: 'Notification' in window ? Notification.permission : 'N/A',
    // Device detection
    isIOS: isIOS(),
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    // PWA/Standalone detection
    'navigator.standalone (iOS key)': iosStandalone,
    'display-mode: standalone': standalone,
    'display-mode: fullscreen': fullscreen,
    isInstalledPWA: standalone || iosStandalone === true || fullscreen,
    // For iOS: pushManager only available when navigator.standalone === true
    userAgent: navigator.userAgent
  };
};

/**
 * Get the current notification permission status
 */
export const getPermissionStatus = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default', 'granted', 'denied'
};

/**
 * Request notification permission from the user
 */
export const requestPermission = async () => {
  if (!isPushSupported()) {
    return { success: false, error: 'Push notifications not supported' };
  }

  try {
    const permission = await Notification.requestPermission();
    return { success: permission === 'granted', permission };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Register the service worker
 */
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    return { success: false, error: 'Service workers not supported' };
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    console.log('Service worker registered:', registration.scope);

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;

    return { success: true, registration };
  } catch (error) {
    console.error('Service worker registration failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get the VAPID public key from the server
 */
export const getVapidPublicKey = async () => {
  try {
    const response = await fetch(`${API_URL}/api/push/vapid-public-key`);
    if (!response.ok) {
      if (response.status === 503) {
        throw new Error('Push-Benachrichtigungen sind nicht konfiguriert. VAPID-Schlüssel fehlen auf dem Server.');
      }
      throw new Error('Failed to get VAPID public key');
    }
    const data = await response.json();
    if (!data.publicKey) {
      throw new Error('Server returned empty VAPID public key');
    }
    return data.publicKey;
  } catch (error) {
    console.error('[Push] Error fetching VAPID public key:', error);
    throw error;
  }
};

/**
 * Convert VAPID key from base64 to Uint8Array
 */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Subscribe to push notifications
 */
export const subscribeToPush = async (authToken) => {
  console.log('[Push Subscribe] Starting subscription process...');

  try {
    // Ensure service worker is registered and ready
    console.log('[Push Subscribe] Registering service worker...');
    const swResult = await registerServiceWorker();
    if (!swResult.success) {
      console.error('[Push Subscribe] Service worker registration failed:', swResult.error);
      return swResult;
    }

    const registration = await navigator.serviceWorker.ready;
    console.log('[Push Subscribe] Service worker ready');

    // Check if pushManager is available
    if (!registration.pushManager) {
      console.error('[Push Subscribe] pushManager not available on registration');
      return { success: false, error: 'Push manager not available. Make sure app is installed to home screen.' };
    }

    // Check current subscription
    console.log('[Push Subscribe] Checking existing subscription...');
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      console.log('[Push Subscribe] No existing subscription, creating new one...');

      // Get VAPID public key
      console.log('[Push Subscribe] Fetching VAPID public key...');
      const vapidPublicKey = await getVapidPublicKey();
      console.log('[Push Subscribe] Got VAPID key, length:', vapidPublicKey?.length);

      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

      // Create new subscription
      console.log('[Push Subscribe] Creating push subscription...');
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });
      console.log('[Push Subscribe] Push subscription created');
    } else {
      console.log('[Push Subscribe] Using existing subscription');
    }

    console.log('[Push Subscribe] Subscription endpoint:', subscription.endpoint?.substring(0, 50) + '...');

    // Send subscription to server - use toJSON() which provides keys in correct base64url format
    console.log('[Push Subscribe] Sending subscription to server...');
    const subscriptionJson = subscription.toJSON();
    console.log('[Push Subscribe] Subscription keys available:', !!subscriptionJson.keys);

    const response = await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys,
        userAgent: navigator.userAgent
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[Push Subscribe] Server rejected subscription:', errorData);
      throw new Error(errorData.message || 'Failed to save subscription');
    }

    const data = await response.json();
    console.log('Push subscription saved:', data);
    return { success: true, subscription };
  } catch (error) {
    console.error('Error subscribing to push:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPush = async (authToken) => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Notify server
      await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      });
    }

    return { success: true };
  } catch (error) {
    console.error('Error unsubscribing from push:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if user is currently subscribed
 */
export const isSubscribed = async () => {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
};

/**
 * Send a test notification (for debugging)
 */
export const sendTestNotification = async (authToken) => {
  try {
    const response = await fetch(`${API_URL}/api/push/test`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to send test notification');
    }

    return { success: true };
  } catch (error) {
    console.error('Error sending test notification:', error);
    return { success: false, error: error.message };
  }
};
